// Milestone table organised into status-driven sections (Needs attention →
// Awaiting acceptance → Awaiting payment → Upcoming → Completed). Renders a
// single continuous <table> with group-heading <tr> rows interleaved between
// data rows so column geometry stays consistent across sections. Every group
// is collapsible (per-key state persisted to localStorage) and defaults to
// collapsed EXCEPT Needs attention — the grouped view shows the whole list
// (no pagination), so collapse is its volume control. A highlighted or
// expanded row's group force-expands transiently (never persisted) so a deep
// link outside Needs attention is never hidden.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { clsx } from "clsx";
import {
  groupMilestones,
  type MilestoneGroupKey,
} from "./milestoneGrouping";
import type { Milestone } from "../../../../types";
import type { Column } from "../shared/TrackingTable";
import LoadingSpinner from "../../../common/LoadingSpinner";
import Alert from "../../../common/Alert";

const COLLAPSIBLE_KEYS: ReadonlySet<MilestoneGroupKey> = new Set([
  "needsAttention",
  "awaitingAcceptance",
  "awaitingPayment",
  "upcoming",
  "completed",
]);

// Needs attention is the working set, so it alone starts open.
const DEFAULT_COLLAPSED: ReadonlySet<MilestoneGroupKey> = new Set([
  "awaitingAcceptance",
  "awaitingPayment",
  "upcoming",
  "completed",
]);

const COLLAPSE_STORAGE_PREFIX = "milestones.group.";

function readCollapsedFromStorage(key: MilestoneGroupKey): boolean {
  if (typeof window === "undefined") return DEFAULT_COLLAPSED.has(key);
  try {
    const raw = window.localStorage.getItem(`${COLLAPSE_STORAGE_PREFIX}${key}.collapsed`);
    if (raw === "1") return true;
    if (raw === "0") return false;
  } catch {
    // ignore
  }
  return DEFAULT_COLLAPSED.has(key);
}

function writeCollapsedToStorage(key: MilestoneGroupKey, collapsed: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${COLLAPSE_STORAGE_PREFIX}${key}.collapsed`,
      collapsed ? "1" : "0",
    );
  } catch {
    // ignore
  }
}

export interface MilestoneGroupedTableProps {
  items: Milestone[];
  columns: Column<Milestone>[];
  isLoading: boolean;
  error: unknown;
  rowKey: (m: Milestone) => string;
  onRowClick?: (m: Milestone) => void;
  expandedRowId?: string | null;
  highlightRowId?: string | null;
  renderExpandedRow?: (m: Milestone) => React.ReactNode;
  rowClassName?: (m: Milestone) => string;
  today: string;
}

export default function MilestoneGroupedTable({
  items,
  today,
  columns,
  isLoading,
  error,
  rowKey,
  onRowClick,
  expandedRowId,
  highlightRowId,
  renderExpandedRow,
  rowClassName,
}: MilestoneGroupedTableProps) {
  // All hooks must run before any conditional return (React rules of hooks).
  const [collapsed, setCollapsed] = useState<Record<MilestoneGroupKey, boolean>>(() => ({
    needsAttention: readCollapsedFromStorage("needsAttention"),
    awaitingAcceptance: readCollapsedFromStorage("awaitingAcceptance"),
    awaitingPayment: readCollapsedFromStorage("awaitingPayment"),
    upcoming: readCollapsedFromStorage("upcoming"),
    completed: readCollapsedFromStorage("completed"),
  }));

  const toggle = useCallback((key: MilestoneGroupKey) => {
    setCollapsed((prev) => {
      const next = !prev[key];
      writeCollapsedToStorage(key, next);
      return { ...prev, [key]: next };
    });
  }, []);

  const highlightRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (highlightRowId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightRowId]);

  // Render-time adjustment (prev-value guard, not an effect): when a
  // highlighted/expanded row sits inside a collapsed group, expand that
  // group so the reveal is never invisible. Applied once per reveal id and
  // deliberately NOT persisted — following a deep link shouldn't durably
  // change the user's collapse preferences.
  const revealId = expandedRowId ?? highlightRowId ?? null;
  const [appliedRevealId, setAppliedRevealId] = useState<string | null>(null);
  if (revealId !== appliedRevealId) {
    if (!revealId) {
      setAppliedRevealId(null);
    } else {
      const owner = groupMilestones(items, today).find((g) =>
        g.items.some((i) => rowKey(i) === revealId),
      );
      // Consume the reveal only once its owner group exists: on a cold
      // grouped-view deep link the id arrives from the URL while the list
      // is still loading, and consuming it against the empty list would
      // strand the target in a collapsed group after load.
      if (owner) {
        setAppliedRevealId(revealId);
        if (collapsed[owner.key]) {
          setCollapsed((prev) => ({ ...prev, [owner.key]: false }));
        }
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        {error instanceof Error ? error.message : "Failed to load milestones."}
      </Alert>
    );
  }

  const groups = groupMilestones(items, today);
  const colCount = columns.length;

  const visibleGroups = groups.filter((g) => g.items.length > 0);

  return (
    <div
      className="overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      data-testid="milestone-grouped-table"
    >
      <table className="w-full table-fixed">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-800">
            {columns.map((col, i) => (
              <th
                key={i}
                className={clsx(
                  "px-4 py-3 text-overline uppercase text-slate-600 dark:text-slate-400",
                  col.align === "right" ? "text-right" : "text-left",
                  col.width,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        {/* One <tbody> per group so aria-controls resolves to a single element
            and row ids are never duplicated across groups. */}
        {visibleGroups.map((group) => {
          const collapsible = COLLAPSIBLE_KEYS.has(group.key);
          // Guarded on collapsible (parity with DeliverableGroupedView) so a
          // stray stored value could never hide a group with no reopen
          // affordance.
          const isCollapsed = collapsible && collapsed[group.key];
          const bodyId = `milestone-group-body-${group.key}`;
          const Icon = isCollapsed ? ChevronRight : ChevronDown;

          const headingContent = (
            <span className="inline-flex items-center gap-2">
              {collapsible && (
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              <span className="text-overline uppercase text-slate-700 dark:text-slate-200">
                {group.title}
              </span>
              <span
                data-testid={`milestone-group-count-${group.key}`}
                className="rounded-full bg-slate-200 px-2 py-0.5 text-caption text-slate-600 dark:bg-slate-700 dark:text-slate-300"
              >
                {group.items.length}
              </span>
              {group.subLabel && (
                <span className="text-caption text-slate-500 dark:text-slate-400">
                  · {group.subLabel}
                </span>
              )}
            </span>
          );

          return (
            <tbody
              key={group.key}
              id={bodyId}
              className="[&>tr:not(:first-child)]:border-t [&>tr:not(:first-child)]:border-t-slate-200 dark:[&>tr:not(:first-child)]:border-t-slate-700"
            >
              {/* Group heading row */}
              <tr data-testid={`milestone-group-${group.key}`}>
                <td
                  colSpan={colCount}
                  className="bg-slate-200/70 dark:bg-slate-900/70 px-2 py-1.5"
                >
                  {collapsible ? (
                    <button
                      type="button"
                      aria-expanded={!isCollapsed}
                      aria-controls={bodyId}
                      onClick={() => toggle(group.key)}
                      className="flex w-full items-center justify-between rounded-md px-0 py-0 text-left hover:bg-slate-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:hover:bg-slate-700/50"
                      data-testid={`milestone-group-header-${group.key}`}
                    >
                      {headingContent}
                      <span className="sr-only">{isCollapsed ? "Expand" : "Collapse"}</span>
                    </button>
                  ) : (
                    <div data-testid={`milestone-group-header-${group.key}`}>
                      {headingContent}
                    </div>
                  )}
                </td>
              </tr>
              {/* Data rows — hidden when collapsed */}
              {!isCollapsed &&
                group.items.map((item) => {
                  const key = rowKey(item);
                  const isHighlighted = key === highlightRowId;
                  const isExpanded = key === expandedRowId;
                  const isClickable = !!onRowClick;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        data-testid={`tracking-row-${key}`}
                        ref={isHighlighted ? highlightRef : null}
                        className={clsx(
                          "hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors",
                          isHighlighted &&
                            "ring-2 ring-brand-100 dark:ring-brand-900 transition-all duration-300",
                          // Selected (expanded) row: connected brand border +
                          // darker neutral fill, consistent with the other
                          // tracking tables.
                          isExpanded &&
                            "border-l-2 border-brand-500 bg-slate-100 dark:bg-slate-700",
                          rowClassName?.(item),
                        )}
                      >
                        {columns.map((col, ci) => {
                          const wrapWithButton =
                            isClickable && (ci === 0 || col.clickable === true);
                          return (
                            <td
                              key={ci}
                              role="cell"
                              className={clsx(
                                "px-4 py-3 text-body",
                                col.align === "right" ? "text-right" : "text-left",
                                col.cellClassName,
                              )}
                            >
                              {wrapWithButton ? (
                                <button
                                  type="button"
                                  onClick={() => onRowClick!(item)}
                                  className="w-full text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm"
                                >
                                  {col.cell(item)}
                                </button>
                              ) : (
                                col.cell(item)
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {isExpanded && renderExpandedRow && (
                        <tr data-testid={`tracking-row-${key}-expanded`}>
                          <td
                            role="cell"
                            colSpan={colCount}
                            className="bg-slate-50 dark:bg-slate-800/40 border-l-2 border-brand-500 rounded-r-md p-3"
                          >
                            {renderExpandedRow(item)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}
